/**
 * What running the Visual view's statement means: its exact source text, the
 * render-mode `kind` for `feature:vqe_execute`, and — for a MERGE on an engine
 * that has none (mysql/sqlite) — a warning routed through the SAME destructive
 * confirm dialog the SQL editor uses (EDG-02/04: warn, never block; the user
 * may be drafting it for another engine).
 */
import type { DatabaseEngineId, DangerousQueryResult } from '../../../../core/engines';
import { databaseEngineIdToSqlEngine } from '../../../../core/engines/databaseEngineIdToSqlEngine';
import type { StatementInfo } from '../../../sql-grammar/model';
import { ENGINE_DISPLAY_NAME, supportsMerge } from '../dialect/statementCapabilities';
import type { RenderMode } from '../types';
import { vqeCopy } from '../ux/vqeCopy';
import { sliceActiveStatement } from './sliceActiveStatement';

export type VisualStatementRun = {
  readonly sql: string;
  readonly kind: RenderMode;
  /** Ask before running even when the risk gate would not (unsupported MERGE). */
  readonly confirmWith: DangerousQueryResult | null;
};

export function mergeExecuteWarning(
  renderMode: RenderMode | null,
  engineId: DatabaseEngineId,
  sql: string,
): DangerousQueryResult | null {
  const engine = databaseEngineIdToSqlEngine(engineId);
  if (renderMode !== 'merge' || !engine || supportsMerge(engine)) return null;
  return {
    dangerous: true,
    severity: 'high',
    reason: vqeCopy.capability.merge.executeWarning(ENGINE_DISPLAY_NAME[engine]),
    statement: sql.slice(0, 100),
  };
}

export function visualStatementRun(
  bufferText: string,
  statement: StatementInfo | null,
  renderMode: RenderMode | null,
  engineId: DatabaseEngineId,
): VisualStatementRun {
  const sql = sliceActiveStatement(bufferText, statement);
  return {
    sql,
    kind: renderMode ?? 'parse-error',
    confirmWith: mergeExecuteWarning(renderMode, engineId, sql),
  };
}
